import React, { useState } from 'react';
import { PlusIcon, FileTextIcon, ArrowUpDownIcon, InfoIcon } from 'lucide-react';
type ActivityTab = 'payouts' | 'all-activity';
export function Balance() {
  const [activeTab, setActiveTab] = useState<ActivityTab>('payouts');
  const [showTooltip, setShowTooltip] = useState(false);
  // Balance data matching BalanceSnapshotWidget
  const balanceData = {
    totalBalance: 77467.25,
    incoming: 0.0,
    available: 77467.25,
    upcomingPayout: 45230.8,
    currency: 'MYR',
    payoutDate: '5 Dec 2025'
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: balanceData.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  // Sample payout data with updated dates (1-6 Dec 2025)
  const payouts = [{
    amount: 59.47,
    destination: 'MALAYAN BANKING BERHAD •••• 1005',
    arriveBy: 'Dec 6, 2025',
    status: 'Paid'
  }, {
    amount: 2624.57,
    destination: 'MALAYAN BANKING BERHAD •••• 1005',
    arriveBy: 'Dec 5, 2025',
    status: 'Paid'
  }, {
    amount: 29.83,
    destination: 'MALAYAN BANKING BERHAD •••• 1005',
    arriveBy: 'Dec 4, 2025',
    status: 'Paid'
  }, {
    amount: 29.52,
    destination: 'MALAYAN BANKING BERHAD •••• 1005',
    arriveBy: 'Dec 3, 2025',
    status: 'Paid'
  }, {
    amount: 35.47,
    destination: 'MALAYAN BANKING BERHAD •••• 1005',
    arriveBy: 'Dec 2, 2025',
    status: 'Paid'
  }];
  // Sample all activity data with updated dates (1-6 Dec 2025)
  const allActivity = [{
    amount: 4.8,
    fees: 0,
    total: 4.8,
    type: 'Refund failure',
    description: 'Credit for failed refund re_1PxiVqJsvcJaHSqVTj8o58Aw',
    created: 'Dec 6, 2025',
    availableOn: 'Dec 6, 2025'
  }, {
    amount: 12.8,
    fees: 0,
    total: 12.8,
    type: 'Refund failure',
    description: 'Credit for failed refund re_1PxiWPJsvcJaHSqVgeh2UdgC',
    created: 'Dec 5, 2025',
    availableOn: 'Dec 5, 2025'
  }, {
    amount: -12.8,
    fees: 0,
    total: -12.8,
    type: 'Refund',
    description: 'REFUND FOR CHARGE',
    created: 'Dec 4, 2025',
    availableOn: 'Dec 4, 2025'
  }, {
    amount: -4.8,
    fees: 0,
    total: -4.8,
    type: 'Refund',
    description: 'REFUND FOR CHARGE',
    created: 'Dec 3, 2025',
    availableOn: 'Dec 3, 2025'
  }, {
    amount: 0.01,
    fees: 0,
    total: 0.01,
    type: 'Refund failure',
    description: 'Credit for failed refund re_1CWawtJsvcJaHSqV9o2dUZWM',
    created: 'Dec 2, 2025',
    availableOn: 'Dec 2, 2025'
  }];
  return <div className="max-w-7xl mx-auto" data-id="element-3845">
      {/* Header */}
      <div className="mb-8" data-id="element-3846">
        <div className="flex items-start gap-3 mb-4" data-id="element-3847">
          <div data-id="element-3848">
            <div className="flex items-center gap-2 mb-2" data-id="element-3849">
              <h1 className="text-4xl font-bold text-gray-900" data-id="element-3850">Balances</h1>
              <div className="relative" data-id="element-3851">
                <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-3852">
                  <InfoIcon size={20} className="text-gray-400" data-id="element-3853" />
                </button>
                {showTooltip && <div className="absolute left-0 top-full mt-2 w-96 p-4 bg-white border border-gray-200 rounded-lg shadow-xl z-50" data-id="element-3854">
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-3855">
                      The sum of your available payments balances converted to
                      your default currency using Maybank exchange rates.
                    </p>
                  </div>}
              </div>
            </div>
            <span className="text-4xl font-bold text-gray-900" data-id="element-3856">
              {formatCurrency(balanceData.totalBalance)}
            </span>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-3857">
          <PlusIcon size={16} data-id="element-3858" />
          Add funds
        </button>
      </div>

      <div className="flex gap-8" data-id="element-3859">
        {/* Main Content */}
        <div className="flex-1" data-id="element-3860">
          {/* Balance Summary */}
          <div className="mb-8" data-id="element-3861">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3862">
              Balance summary
            </h2>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3863">
              {/* Purple bar */}
              <div className="h-2 bg-purple-500" data-id="element-3864"></div>

              {/* Table */}
              <div className="divide-y divide-gray-200" data-id="element-3865">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3 bg-gray-50" data-id="element-3866">
                  <span className="text-sm font-medium text-gray-700" data-id="element-3867">
                    Payments type
                  </span>
                  <span className="text-sm font-medium text-gray-700" data-id="element-3868">
                    Amount
                  </span>
                </div>

                {/* Incoming */}
                <div className="flex items-center justify-between px-6 py-4" data-id="element-3869">
                  <div className="flex items-center gap-3" data-id="element-3870">
                    <div className="w-3 h-3 bg-gray-300 rounded-sm" data-id="element-3871"></div>
                    <span className="text-sm text-gray-900" data-id="element-3872">Incoming</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-3873">
                    {formatCurrency(balanceData.incoming)}
                  </span>
                </div>

                {/* Available */}
                <div className="flex items-center justify-between px-6 py-4" data-id="element-3874">
                  <div className="flex items-center gap-3" data-id="element-3875">
                    <div className="w-3 h-3 bg-purple-500 rounded-sm" data-id="element-3876"></div>
                    <span className="text-sm text-gray-900" data-id="element-3877">Available</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-3878">
                    {formatCurrency(balanceData.available)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div data-id="element-3879">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3880">
              Recent activity
            </h2>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6" data-id="element-3881">
              <div className="flex gap-6" data-id="element-3882">
                <button onClick={() => setActiveTab('payouts')} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'payouts' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-3883">
                  Payouts
                </button>
                <button onClick={() => setActiveTab('all-activity')} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'all-activity' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-3884">
                  All activity
                </button>
              </div>
            </div>

            {/* Payouts Table */}
            {activeTab === 'payouts' && <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3885">
                <table className="w-full" data-id="element-3886">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-3887">
                    <tr data-id="element-3888">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3889">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3890">
                        Destination
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-700" data-id="element-3891">
                        Arrive by
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-3892">
                    {payouts.map((payout, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-3893">
                        <td className="px-6 py-4" data-id="element-3894">
                          <div className="flex items-center gap-2" data-id="element-3895">
                            <span className="text-sm font-medium text-gray-900" data-id="element-3896">
                              {formatCurrency(payout.amount)}
                            </span>
                            <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 rounded" data-id="element-3897">
                              {payout.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3898">
                          {payout.destination}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 text-right" data-id="element-3899">
                          {payout.arriveBy}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="px-6 py-4 border-t border-gray-200" data-id="element-3900">
                  <button className="text-sm font-medium text-purple-600 hover:text-purple-700" data-id="element-3901">
                    View more
                  </button>
                </div>
              </div>}

            {/* All Activity Table */}
            {activeTab === 'all-activity' && <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3902">
                <table className="w-full" data-id="element-3903">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-3904">
                    <tr data-id="element-3905">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3906">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3907">
                        Fees
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3908">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3909">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3910">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3911">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700" data-id="element-3912">
                        Available on
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-3913">
                    {allActivity.map((activity, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-3914">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-3915">
                          {formatCurrency(activity.amount)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3916">—</td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-3917">
                          {formatCurrency(activity.total)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3918">
                          {activity.type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" data-id="element-3919">
                          {activity.description}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3920">
                          {activity.created}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-3921">
                          {activity.availableOn}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="px-6 py-4 border-t border-gray-200" data-id="element-3922">
                  <button className="text-sm font-medium text-purple-600 hover:text-purple-700" data-id="element-3923">
                    View more
                  </button>
                </div>
              </div>}
          </div>
        </div>

        {/* Sidebar - Reports */}
        <div className="w-80 flex-shrink-0" data-id="element-3924">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3925">Reports</h3>
          <div className="space-y-3" data-id="element-3926">
            <button className="w-full flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all text-left" data-id="element-3927">
              <FileTextIcon size={20} className="text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3928" />
              <div data-id="element-3929">
                <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-3930">
                  Balance summary
                </div>
                <div className="text-xs text-gray-500" data-id="element-3931">Dec 2025</div>
              </div>
            </button>
            <button className="w-full flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all text-left" data-id="element-3932">
              <ArrowUpDownIcon size={20} className="text-gray-400 flex-shrink-0 mt-0.5" data-id="element-3933" />
              <div data-id="element-3934">
                <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-3935">
                  Payout reconciliation
                </div>
                <div className="text-xs text-gray-500" data-id="element-3936">Dec 2025</div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>;
}